// Remember view pure derivation. This probe-clean leaf is the only place that
// decides which authored moments may appear in Remember.
import type { Prayer, ProgressEntry } from '../types/prayer';
import {
  emptyIdentityMap,
  GENERAL_KEY,
  isGeneral,
  isSelf,
  isPraise,
  whoOfWithMap,
  type WhoMergeMap,
} from './who-match';
import { progressEntryIds } from './progress-entry-identity';
import {
  compareDiscoveryStrings,
  discoveryField,
  discoveryWordQuality,
  prepareDiscoveryQuery,
  scoreDiscoveryFields,
  type DiscoveryField,
  type DiscoveryQuery,
} from './discovery-search';
import { MAX_REMEMBER_SYNONYM_TERMS } from './topic-search';

export const REMEMBER_FUTURE_SKEW_MS = 24 * 60 * 60 * 1000;
export const RAW_PRAYER_EXCERPT_CHARS = 320;

export type MomentKind = 'progress' | 'answered' | 'testimony';
export type MomentBodySource =
  | { kind: 'progress-note'; entryId: string }
  | { kind: 'testimony' }
  | { kind: 'raw-prayer-excerpt'; start: 0; end: number }
  | { kind: 'none' };
export type MomentDateSource =
  | 'progress-entry'
  | 'answered-at'
  | 'testimony-written-at'
  | 'reanswered-at'
  | 'legacy-updated-at-proxy';

type MomentBase = {
  id: string;
  prayer: Prayer;
  date: string;
  dateSource: MomentDateSource;
  body: string | null;
  bodySource: MomentBodySource;
};

export type Moment =
  | (MomentBase & { kind: 'progress'; entry: ProgressEntry; entryId: string })
  | (MomentBase & { kind: 'answered' })
  | (MomentBase & { kind: 'testimony' });

function momentStatusOf(prayer: Prayer): string {
  return prayer.status ?? 'Open';
}

function finiteDate(value: unknown, nowMs: number): { value: string; time: number } | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const time = new Date(value).getTime();
  if (!Number.isFinite(time) || time > nowMs + REMEMBER_FUTURE_SKEW_MS) return null;
  return { value, time };
}

function boundedRawPrayerExcerpt(rawText: string): {
  body: string | null;
  source: MomentBodySource;
} {
  const normalized = rawText.replace(/\s+/g, ' ').trim();
  if (!normalized) return { body: null, source: { kind: 'none' } };
  const characters = Array.from(normalized);
  if (characters.length <= RAW_PRAYER_EXCERPT_CHARS) {
    return {
      body: normalized,
      source: { kind: 'raw-prayer-excerpt', start: 0, end: characters.length },
    };
  }
  const body = `${characters.slice(0, RAW_PRAYER_EXCERPT_CHARS - 1).join('').trimEnd()}…`;
  return {
    body,
    source: { kind: 'raw-prayer-excerpt', start: 0, end: RAW_PRAYER_EXCERPT_CHARS - 1 },
  };
}

function answeredBody(prayer: Prayer, entryIds: readonly string[]): {
  body: string | null;
  source: MomentBodySource;
} {
  const history = prayer.progressHistory ?? [];
  const positiveIndex = history.findIndex(
    (entry) => entry.recommendedStage === 'God is moving' && entry.note.trim() !== '',
  );
  if (positiveIndex >= 0) {
    return {
      body: history[positiveIndex].note.trim(),
      source: { kind: 'progress-note', entryId: entryIds[positiveIndex] },
    };
  }
  return boundedRawPrayerExcerpt(prayer.rawText ?? '');
}

function answeredDate(prayer: Prayer, nowMs: number) {
  const anchored = finiteDate(prayer.answeredAt, nowMs);
  if (anchored) return { ...anchored, source: 'answered-at' as const };
  const proxy = finiteDate(prayer.updatedAt, nowMs);
  return proxy ? { ...proxy, source: 'legacy-updated-at-proxy' as const } : null;
}

function testimonyDate(prayer: Prayer, nowMs: number) {
  const testimony = finiteDate(prayer.testimonyWrittenAt, nowMs);
  const answer = finiteDate(prayer.answeredAt, nowMs);
  if (answer && (!testimony || answer.time > testimony.time)) {
    return { ...answer, source: 'reanswered-at' as const };
  }
  if (testimony) return { ...testimony, source: 'testimony-written-at' as const };
  const proxy = finiteDate(prayer.updatedAt, nowMs);
  return proxy ? { ...proxy, source: 'legacy-updated-at-proxy' as const } : null;
}

export function deriveMoments(
  prayers: Prayer[],
  nowMs: number = Date.now(),
): Moment[] {
  const out: Moment[] = [];
  for (const prayer of prayers) {
    const history = prayer.progressHistory ?? [];
    const entryIds = progressEntryIds(prayer.id, history);
    for (let index = 0; index < history.length; index += 1) {
      const entry = history[index];
      if (entry.recommendedStage !== 'God is moving') continue;
      const date = finiteDate(entry.date, nowMs);
      if (!date) continue;
      const body = entry.note.trim() || null;
      const entryId = entryIds[index];
      out.push({
        kind: 'progress',
        id: `progress:${prayer.id}:${entryId}`,
        prayer,
        entry,
        entryId,
        date: date.value,
        dateSource: 'progress-entry',
        body,
        bodySource: body
          ? { kind: 'progress-note', entryId }
          : { kind: 'none' },
      });
    }

    if (momentStatusOf(prayer) !== 'Answered') continue;
    const testimony = prayer.testimony?.trim();
    if (testimony) {
      const date = testimonyDate(prayer, nowMs);
      if (date) {
        out.push({
          kind: 'testimony',
          id: `testimony:${prayer.id}:${date.value}`,
          prayer,
          date: date.value,
          dateSource: date.source,
          body: testimony,
          bodySource: { kind: 'testimony' },
        });
      }
      continue;
    }

    const date = answeredDate(prayer, nowMs);
    if (!date) continue;
    const body = answeredBody(prayer, entryIds);
    out.push({
      kind: 'answered',
      id: `answered:${prayer.id}:${date.value}`,
      prayer,
      date: date.value,
      dateSource: date.source,
      body: body.body,
      bodySource: body.source,
    });
  }

  out.sort((left, right) => {
    const timeOrder = new Date(right.date).getTime() - new Date(left.date).getTime();
    return timeOrder || left.id.localeCompare(right.id);
  });
  return out;
}

export type StreamItem =
  | { kind: 'year'; key: string; year: number }
  | { kind: 'moment'; key: string; moment: Moment };

export function interleaveYearMarkers(moments: Moment[]): StreamItem[] {
  const out: StreamItem[] = [];
  let lastYear: number | null = null;
  for (const moment of moments) {
    const year = new Date(moment.date).getFullYear();
    if (year !== lastYear) {
      out.push({ kind: 'year', key: `year:${year}`, year });
      lastYear = year;
    }
    out.push({ kind: 'moment', key: moment.id, moment });
  }
  return out;
}

export function momentBody(moment: Moment): string | null {
  return moment.body;
}

export interface MomentThread {
  key: string;
  isSelf: boolean;
  moments: Moment[];
  count: number;
  latestDate: string;
}

export function groupMomentsByPerson(
  moments: Moment[],
  mergeMap: WhoMergeMap,
): MomentThread[] {
  const groups = new Map<string, Moment[]>();
  for (const moment of moments) {
    const who = whoOfWithMap(moment.prayer, mergeMap);
    if (isGeneral(who)) continue;
    const bucket = groups.get(who);
    if (bucket) bucket.push(moment);
    else groups.set(who, [moment]);
  }
  const threads = Array.from(groups, ([key, grouped]) => ({
    key,
    isSelf: isSelf(key),
    moments: grouped,
    count: grouped.length,
    latestDate: grouped[0].date,
  }));
  threads.sort((left, right) => {
    const timeOrder =
      new Date(right.latestDate).getTime() - new Date(left.latestDate).getTime();
    return timeOrder || left.key.localeCompare(right.key);
  });
  return threads;
}

export interface RememberSearchMetrics {
  normalizedPrayerCount: number;
  normalizedProgressEntryCount: number;
  prayerMatchChecks: number;
  expandedMomentChecks: number;
  normalizedMomentCount: number;
  indexedTokenCount: number;
  postingCount: number;
  tokenComparisons: number;
  postingVisits: number;
  momentEvaluations: number;
  relatedTermCount: number;
  prayerContextEvaluations: number;
  truncatedContextFieldCount: number;
  skippedOverlongContextTokenCount: number;
  retainedBytesEstimate: number;
}

export const REMEMBER_SEARCH_MAX_CONTEXT_FIELD_CHARACTERS = 32_768;
export const REMEMBER_SEARCH_MAX_INDEX_TOKEN_LENGTH = 256;

export interface RememberSearchIndex {
  readonly scopeKey: string;
  /** Opaque rebuild identity that does not retain the prior prayer corpus. */
  readonly revision: symbol;
  readonly moments: readonly Moment[];
  readonly fields: readonly RememberMomentFields[];
  readonly tokenList: readonly string[];
  readonly postings: readonly (readonly number[])[];
  readonly prayerIds: readonly string[];
  readonly prayerFields: readonly RememberPrayerFields[];
  readonly representativeMomentIndexes: readonly number[];
  readonly momentPrayerIndexes: readonly number[];
  readonly prayerTokenList: readonly string[];
  readonly prayerPostings: readonly (readonly number[])[];
  readonly retainedBytesEstimate: number;
}

interface RememberMomentFields {
  readonly name: DiscoveryField;
  readonly body: DiscoveryField;
}

interface RememberPrayerFields {
  /** Displayed/raw who plus every parsed mentioned person. */
  readonly identity: DiscoveryField;
  /** AI-derived recall signals: gardenSummary + parsed core prayer. */
  readonly summary: DiscoveryField;
  readonly raw: DiscoveryField;
  readonly updates: DiscoveryField;
  readonly testimony: DiscoveryField;
}

export interface RememberSearchBuildOptions {
  scopeKey?: string;
  mergeMap?: WhoMergeMap;
  labels?: { self: string; general: string; praise?: string };
}

export function createRememberSearchMetrics(): RememberSearchMetrics {
  return {
    normalizedPrayerCount: 0,
    normalizedProgressEntryCount: 0,
    prayerMatchChecks: 0,
    expandedMomentChecks: 0,
    normalizedMomentCount: 0,
    indexedTokenCount: 0,
    postingCount: 0,
    tokenComparisons: 0,
    postingVisits: 0,
    momentEvaluations: 0,
    relatedTermCount: 0,
    prayerContextEvaluations: 0,
    truncatedContextFieldCount: 0,
    skippedOverlongContextTokenCount: 0,
    retainedBytesEstimate: 0,
  };
}

function looksLikeRememberSearchMetrics(
  value: RememberSearchBuildOptions | RememberSearchMetrics | undefined,
): value is RememberSearchMetrics {
  return value !== undefined && 'normalizedPrayerCount' in value;
}

function displayWho(
  prayer: Prayer,
  mergeMap: WhoMergeMap,
  labels: { self: string; general: string; praise?: string },
): string {
  const who = whoOfWithMap(prayer, mergeMap);
  if (isPraise(prayer) && labels.praise) return labels.praise;
  if (isSelf(who)) return labels.self;
  if (isGeneral(who)) return labels.general;
  return who;
}

function boundedContextField(
  parts: Iterable<string | null | undefined>,
  weight: number,
  metrics?: RememberSearchMetrics,
): DiscoveryField {
  let source = '';
  let remaining = REMEMBER_SEARCH_MAX_CONTEXT_FIELD_CHARACTERS;
  let truncated = false;
  for (const part of parts) {
    if (!part) continue;
    if (remaining <= 0) {
      truncated = true;
      break;
    }
    if (source) {
      source += ' ';
      remaining -= 1;
    }
    if (part.length > remaining) {
      source += part.slice(0, Math.max(0, remaining));
      remaining = 0;
      truncated = true;
    } else {
      source += part;
      remaining -= part.length;
    }
  }
  const field = discoveryField(
    source.slice(0, REMEMBER_SEARCH_MAX_CONTEXT_FIELD_CHARACTERS),
    weight,
  );
  const tokens = field.tokens.filter(
    (token) => token.length <= REMEMBER_SEARCH_MAX_INDEX_TOKEN_LENGTH,
  );
  if (metrics) {
    if (truncated) metrics.truncatedContextFieldCount += 1;
    metrics.skippedOverlongContextTokenCount += field.tokens.length - tokens.length;
  }
  return tokens.length === field.tokens.length ? field : { ...field, tokens };
}

function buildPrayerContextFields(
  prayer: Prayer,
  displayedWho: string,
  metrics?: RememberSearchMetrics,
): RememberPrayerFields {
  const history = prayer.progressHistory ?? [];
  if (metrics) metrics.normalizedProgressEntryCount += history.length;
  const updateNotes = {
    *[Symbol.iterator](): Iterator<string> {
      for (const entry of history) yield entry.note;
    },
  };
  const identityParts = {
    *[Symbol.iterator](): Iterator<string | undefined> {
      yield displayedWho;
      yield prayer.parsed?.who;
      for (const person of prayer.parsed?.mentionedPeople ?? []) yield person;
    },
  };
  return {
    identity: boundedContextField(
      identityParts,
      50,
      metrics,
    ),
    summary: boundedContextField(
      [prayer.gardenSummary, prayer.parsed?.core_prayer],
      40,
      metrics,
    ),
    raw: boundedContextField([prayer.rawText], 30, metrics),
    updates: boundedContextField(updateNotes, 20, metrics),
    testimony: boundedContextField([prayer.testimony], 10, metrics),
  };
}

/**
 * Per-prayer normalization cache for the Remember index (P2, garden-remember
 * readiness 2026-10-16). Keyed by the Prayer object (loadPrayers keeps
 * unchanged prayers' objects) and validated against every source the fields
 * are built from, so a changed or foreign Prayer can only miss, never reuse
 * stale text. Used only without metrics: the readiness probes count
 * normalizations and keep the uncached path.
 */
interface RememberPrayerCacheEntry {
  source: readonly (string | null | undefined)[];
  displayedWho: string;
  name: DiscoveryField;
  context: RememberPrayerFields;
  bodies: Map<string, DiscoveryField>;
}
const rememberPrayerCache = new WeakMap<Prayer, RememberPrayerCacheEntry>();

function rememberSource(
  prayer: Prayer,
  entries: readonly ProgressEntry[],
): (string | null | undefined)[] {
  return [
    prayer.parsed?.who,
    ...(prayer.parsed?.mentionedPeople ?? []),
    '\u0000',
    prayer.gardenSummary,
    prayer.parsed?.core_prayer,
    prayer.rawText,
    prayer.testimony,
    '\u0000',
    ...entries.map((entry) => entry.note),
  ];
}

function sameRememberSource(
  cached: readonly (string | null | undefined)[],
  current: readonly (string | null | undefined)[],
): boolean {
  return cached.length === current.length && cached.every((value, index) => value === current[index]);
}

function cachedRememberPrayer(prayer: Prayer, displayedWho: string): RememberPrayerCacheEntry {
  // One pass over the progress history (probe-remember-readiness F03): the
  // snapshot feeds both the source check and, on a miss, the context build.
  const entries = [...(prayer.progressHistory ?? [])];
  const source = rememberSource(prayer, entries);
  const cached = rememberPrayerCache.get(prayer);
  if (cached && cached.displayedWho === displayedWho && sameRememberSource(cached.source, source)) {
    return cached;
  }
  const entry: RememberPrayerCacheEntry = {
    source,
    displayedWho,
    name: discoveryField(displayedWho, 10),
    context: buildPrayerContextFields({ ...prayer, progressHistory: entries }, displayedWho),
    bodies: new Map(),
  };
  rememberPrayerCache.set(prayer, entry);
  return entry;
}

function cachedMomentBody(entry: RememberPrayerCacheEntry, body: string): DiscoveryField {
  let field = entry.bodies.get(body);
  if (!field) {
    field = discoveryField(body, 20);
    entry.bodies.set(body, field);
  }
  return field;
}

const viewRememberIndexCache = new WeakMap<readonly Moment[], {
  scopeKey: string;
  mergeMap: WhoMergeMap;
  self: string;
  general: string;
  praise?: string;
  entries: Map<Prayer, RememberPrayerCacheEntry | undefined>;
  index: RememberSearchIndex;
}>();

/**
 * Remember's index for one exact moments array, scope, merge map and label
 * set, as resumable steps (P2, 2026-10-16): the build yields after each
 * moment or prayer so it can run in idle slices. The finished index is kept
 * per moments array (weakly keyed: a replaced corpus or another account's
 * array never reuses it) and reused only while every prayer still has the
 * exact cache entry it was built from and its sources still match, so a
 * prayer changed in place forces a rebuild.
 */
export function* rememberSearchIndexSteps(
  moments: readonly Moment[],
  options: { scopeKey: string; mergeMap: WhoMergeMap; labels: { self: string; general: string; praise?: string } },
): Generator<void, RememberSearchIndex, void> {
  const cached = viewRememberIndexCache.get(moments);
  if (
    cached &&
    cached.scopeKey === options.scopeKey &&
    cached.mergeMap === options.mergeMap &&
    cached.self === options.labels.self &&
    cached.general === options.labels.general &&
    cached.praise === options.labels.praise
  ) {
    let current = true;
    for (const [prayer, entry] of cached.entries) {
      current = entry !== undefined &&
        rememberPrayerCache.get(prayer) === entry &&
        sameRememberSource(entry.source, rememberSource(prayer, [...(prayer.progressHistory ?? [])]));
      if (!current) break;
      yield;
    }
    if (current) return cached.index;
  }
  const index = yield* buildRememberSearchIndexSteps(moments, options);
  const entries = new Map<Prayer, RememberPrayerCacheEntry | undefined>();
  for (const moment of moments) {
    if (!entries.has(moment.prayer)) entries.set(moment.prayer, rememberPrayerCache.get(moment.prayer));
  }
  viewRememberIndexCache.set(moments, {
    scopeKey: options.scopeKey,
    mergeMap: options.mergeMap,
    self: options.labels.self,
    general: options.labels.general,
    praise: options.labels.praise,
    entries,
    index,
  });
  return index;
}

function prayerContextTiers(fields: RememberPrayerFields): readonly DiscoveryField[] {
  return [fields.identity, fields.summary, fields.raw, fields.updates, fields.testimony];
}

function prayerSemanticTiers(fields: RememberPrayerFields): readonly DiscoveryField[] {
  return [fields.summary, fields.raw, fields.updates, fields.testimony];
}

/**
 * Build a local-only two-level index. Visible attribution/body fields stay
 * moment-scoped; wider prayer context is normalized and retained once per
 * prayer so one prayer with many positive moments cannot duplicate its corpus.
 */
export function buildRememberSearchIndex(
  moments: readonly Moment[],
  optionsOrMetrics: RememberSearchBuildOptions | RememberSearchMetrics = {},
  explicitMetrics?: RememberSearchMetrics,
): RememberSearchIndex {
  const options = looksLikeRememberSearchMetrics(optionsOrMetrics)
    ? {}
    : optionsOrMetrics;
  const metrics = looksLikeRememberSearchMetrics(optionsOrMetrics)
    ? optionsOrMetrics
    : explicitMetrics;
  const steps = buildRememberSearchIndexSteps(moments, options, metrics);
  for (;;) {
    const next = steps.next();
    if (next.done) return next.value;
  }
}

// The one build implementation; it yields after each moment or prayer so the
// same work can run whole (buildRememberSearchIndex) or in idle slices.
function* buildRememberSearchIndexSteps(
  moments: readonly Moment[],
  options: RememberSearchBuildOptions,
  metrics?: RememberSearchMetrics,
): Generator<void, RememberSearchIndex, void> {
  const mergeMap = options.mergeMap ?? emptyIdentityMap();
  const labels = options.labels ?? { self: 'You', general: 'General' };
  const nameByPrayerId = new Map<string, DiscoveryField>();
  const prayerIndexById = new Map<string, number>();
  const fields: RememberMomentFields[] = [];
  const prayerIds: string[] = [];
  const prayerFields: RememberPrayerFields[] = [];
  const representativeMomentIndexes: number[] = [];
  const momentPrayerIndexes: number[] = [];
  const tokenUniverse = new Set<string>();

  for (let momentIndex = 0; momentIndex < moments.length; momentIndex += 1) {
    const moment = moments[momentIndex];
    let name = nameByPrayerId.get(moment.prayer.id);
    if (!name) {
      const displayedWho = displayWho(moment.prayer, mergeMap, labels);
      const cached = metrics ? null : cachedRememberPrayer(moment.prayer, displayedWho);
      name = cached ? cached.name : discoveryField(displayedWho, 10);
      nameByPrayerId.set(moment.prayer.id, name);
      const prayerIndex = prayerIds.length;
      prayerIndexById.set(moment.prayer.id, prayerIndex);
      prayerIds.push(moment.prayer.id);
      prayerFields.push(cached ? cached.context : buildPrayerContextFields(moment.prayer, displayedWho, metrics));
      representativeMomentIndexes.push(momentIndex);
      if (metrics) metrics.normalizedPrayerCount += 1;
    }
    const prayerIndex = prayerIndexById.get(moment.prayer.id)!;
    momentPrayerIndexes.push(prayerIndex);
    const representativeIndex = representativeMomentIndexes[prayerIndex];
    const representative = moments[representativeIndex];
    const momentTime = finiteMomentTime(moment);
    const representativeTime = finiteMomentTime(representative);
    if (
      momentTime > representativeTime ||
      (momentTime === representativeTime && moment.id < representative.id)
    ) representativeMomentIndexes[prayerIndex] = momentIndex;
    const cachedEntry = metrics ? null : rememberPrayerCache.get(moment.prayer);
    const body = cachedEntry
      ? cachedMomentBody(cachedEntry, moment.body ?? '')
      : discoveryField(moment.body ?? '', 20);
    if (metrics) {
      metrics.normalizedMomentCount += 1;
    }
    fields.push({ name, body });
    for (const token of name.tokens) tokenUniverse.add(token);
    for (const token of body.tokens) tokenUniverse.add(token);
    yield;
  }

  const tokenList = [...tokenUniverse].sort();
  const tokenIdByToken = new Map(
    tokenList.map((token, tokenId) => [token, tokenId] as const),
  );
  const postings: number[][] = Array.from({ length: tokenList.length }, () => []);
  for (let momentIndex = 0; momentIndex < fields.length; momentIndex += 1) {
    const masks = new Map<string, number>();
    for (const token of fields[momentIndex].name.tokens) masks.set(token, 1);
    for (const token of fields[momentIndex].body.tokens) {
      masks.set(token, (masks.get(token) ?? 0) | 2);
    }
    for (const [token, mask] of masks) {
      postings[tokenIdByToken.get(token)!].push(momentIndex * 4 + mask);
    }
    yield;
  }

  const prayerTokenUniverse = new Set<string>();
  for (const normalized of prayerFields) {
    for (const tier of prayerContextTiers(normalized)) {
      for (const token of tier.tokens) prayerTokenUniverse.add(token);
    }
    yield;
  }
  const prayerTokenList = [...prayerTokenUniverse].sort();
  const prayerTokenIdByToken = new Map(
    prayerTokenList.map((token, tokenId) => [token, tokenId] as const),
  );
  const prayerPostings: number[][] = Array.from(
    { length: prayerTokenList.length },
    () => [],
  );
  for (let prayerIndex = 0; prayerIndex < prayerFields.length; prayerIndex += 1) {
    const masks = new Map<string, number>();
    const tiers = prayerContextTiers(prayerFields[prayerIndex]);
    for (let tierIndex = 0; tierIndex < tiers.length; tierIndex += 1) {
      for (const token of tiers[tierIndex].tokens) {
        masks.set(token, (masks.get(token) ?? 0) | (1 << tierIndex));
      }
    }
    for (const [token, mask] of masks) {
      prayerPostings[prayerTokenIdByToken.get(token)!].push(prayerIndex * 32 + mask);
    }
    yield;
  }

  const postingCount = postings.reduce((total, list) => total + list.length, 0);
  const prayerPostingCount = prayerPostings.reduce(
    (total, list) => total + list.length,
    0,
  );
  const retainedBytesEstimate =
    fields.reduce(
      (total, value) =>
        total + value.body.normalized.length * 2 + value.body.tokens.length * 8 + 48,
      0,
    ) +
    [...nameByPrayerId.values()].reduce(
      (total, value) =>
        total + value.normalized.length * 2 + value.tokens.length * 8 + 40,
      0,
    ) +
    tokenList.reduce((total, token) => total + token.length * 2 + 16, 0) +
    postingCount * 4 +
    prayerFields.reduce(
      (total, value) =>
        total + prayerContextTiers(value).reduce(
          (fieldTotal, field) =>
            fieldTotal + field.normalized.length * 2 + field.tokens.length * 8 + 40,
          0,
        ),
      0,
    ) +
    prayerTokenList.reduce((total, token) => total + token.length * 2 + 16, 0) +
    prayerPostingCount * 4 +
    moments.length * 32 +
    prayerIds.length * 64;
  if (metrics) {
    metrics.indexedTokenCount = tokenList.length + prayerTokenList.length;
    metrics.postingCount = postingCount + prayerPostingCount;
    metrics.retainedBytesEstimate = retainedBytesEstimate;
  }
  return {
    scopeKey: options.scopeKey ?? 'local',
    revision: Symbol('remember-search-index'),
    moments: [...moments],
    fields,
    tokenList,
    postings,
    prayerIds,
    prayerFields,
    representativeMomentIndexes,
    momentPrayerIndexes,
    prayerTokenList,
    prayerPostings,
    retainedBytesEstimate,
  };
}

export type RememberSearchMatchKind = 'direct' | 'context' | 'related';

export interface RememberSearchResult {
  moments: Moment[];
  total: number;
  directTotal: number;
  contextTotal: number;
  relatedTotal: number;
  context: boolean;
  related: boolean;
  matchKindByMomentId: ReadonlyMap<string, RememberSearchMatchKind>;
  scopeKey: string;
  normalizedQuery: string;
  queryTokens: string[];
}

interface RankedMoment {
  momentIndex: number;
  score: number;
}

interface RememberRankOptions {
  bodyOnly: boolean;
  allowSubstring: boolean;
  exactTokensOnly: boolean;
}

const DIRECT_RANK_OPTIONS: RememberRankOptions = {
  bodyOnly: false,
  allowSubstring: true,
  exactTokensOnly: false,
};

const RELATED_RANK_OPTIONS: RememberRankOptions = {
  bodyOnly: true,
  allowSubstring: false,
  exactTokensOnly: true,
};

function exactSortedTokenId(
  values: readonly string[],
  word: string,
  metrics?: RememberSearchMetrics,
): number | undefined {
  let low = 0;
  let high = values.length;
  while (low < high) {
    if (metrics) metrics.tokenComparisons += 1;
    const middle = (low + high) >> 1;
    if (values[middle] < word) low = middle + 1;
    else high = middle;
  }
  return values[low] === word ? low : undefined;
}

function matchingRememberTokenIds(
  word: string,
  index: RememberSearchIndex,
  allowSubstring: boolean,
  exactTokensOnly: boolean,
  metrics?: RememberSearchMetrics,
): number[] {
  if (exactTokensOnly) {
    const tokenId = exactSortedTokenId(index.tokenList, word, metrics);
    return tokenId === undefined ? [] : [tokenId];
  }
  const matched: number[] = [];
  for (let tokenId = 0; tokenId < index.tokenList.length; tokenId += 1) {
    if (metrics) metrics.tokenComparisons += 1;
    const token = index.tokenList[tokenId];
    if (
      token === word ||
      (!exactTokensOnly && token.startsWith(word)) ||
      (!exactTokensOnly && allowSubstring && word.length >= 4 && token.includes(word))
    ) matched.push(tokenId);
  }
  return matched;
}

function postingEstimate(
  tokenIds: readonly number[],
  index: RememberSearchIndex,
): number {
  let estimate = 0;
  for (const tokenId of tokenIds) estimate += index.postings[tokenId].length;
  return estimate;
}

function candidatesForWord(
  word: string,
  tokenIds: readonly number[],
  index: RememberSearchIndex,
  current: Set<number> | null,
  options: RememberRankOptions,
  metrics?: RememberSearchMetrics,
): Set<number> {
  const next = new Set<number>();
  if (current) {
    for (const momentIndex of current) {
      if (metrics) metrics.tokenComparisons += 1;
      const fields = index.fields[momentIndex];
      const bodyQuality = discoveryWordQuality(word, fields.body);
      const nameQuality = options.bodyOnly
        ? 0
        : discoveryWordQuality(word, fields.name);
      if (
        (bodyQuality > 0 &&
          (options.exactTokensOnly
            ? bodyQuality === 3
            : options.allowSubstring || bodyQuality > 1)) ||
        (nameQuality > 0 &&
          (options.exactTokensOnly
            ? nameQuality === 3
            : options.allowSubstring || nameQuality > 1))
      ) next.add(momentIndex);
    }
    return next;
  }
  for (const tokenId of tokenIds) {
    for (const posting of index.postings[tokenId]) {
      if (metrics) metrics.postingVisits += 1;
      if (options.bodyOnly && (posting & 2) === 0) continue;
      next.add(Math.floor(posting / 4));
    }
  }
  return next;
}

function scoreRememberFields(
  query: DiscoveryQuery,
  fields: readonly DiscoveryField[],
  exactTokensOnly: boolean,
): number | null {
  if (!exactTokensOnly) return scoreDiscoveryFields(query, fields);
  let score = 0;
  for (const word of query.tokens) {
    let best = 0;
    for (const field of fields) {
      if (discoveryWordQuality(word, field) === 3) {
        best = Math.max(best, 300 + field.weight);
      }
    }
    if (best === 0) return null;
    score += best;
  }
  return score;
}

function finiteMomentTime(moment: Moment): number {
  const value = Date.parse(moment.date);
  return Number.isFinite(value) ? value : 0;
}

function compareRankedMoments(
  left: RankedMoment,
  right: RankedMoment,
  index: RememberSearchIndex,
): number {
  if (right.score !== left.score) return right.score - left.score;
  const date =
    finiteMomentTime(index.moments[right.momentIndex]) -
    finiteMomentTime(index.moments[left.momentIndex]);
  if (date !== 0) return date;
  return compareDiscoveryStrings(
    index.moments[left.momentIndex].id,
    index.moments[right.momentIndex].id,
  );
}

function rankRememberQuery(
  query: DiscoveryQuery,
  index: RememberSearchIndex,
  metrics?: RememberSearchMetrics,
  options: RememberRankOptions = DIRECT_RANK_OPTIONS,
  sortResults = true,
): RankedMoment[] {
  if (!query.valid || query.tokens.length === 0) return [];
  const groups = query.tokens.map((word) => ({
    word,
    tokenIds: matchingRememberTokenIds(
      word,
      index,
      options.allowSubstring,
      options.exactTokensOnly,
      metrics,
    ),
  }));
  if (groups.some((group) => group.tokenIds.length === 0)) return [];
  groups.sort(
    (left, right) =>
      postingEstimate(left.tokenIds, index) - postingEstimate(right.tokenIds, index),
  );

  let candidates: Set<number> | null = null;
  for (const group of groups) {
    candidates = candidatesForWord(
      group.word,
      group.tokenIds,
      index,
      candidates,
      options,
      metrics,
    );
    if (candidates.size === 0) return [];
  }
  if (!candidates) return [];

  const ranked: RankedMoment[] = [];
  for (const momentIndex of candidates) {
    if (metrics) {
      metrics.momentEvaluations += 1;
      metrics.prayerMatchChecks += 1;
      metrics.expandedMomentChecks += 1;
    }
    const fields = index.fields[momentIndex];
    const rankedFields = options.bodyOnly
      ? [fields.body]
      : [fields.body, fields.name];
    const score = scoreRememberFields(
      query,
      rankedFields,
      options.exactTokensOnly,
    );
    if (score === null) continue;
    const phraseBonus =
      fields.body.normalized === query.normalized ||
      (!options.bodyOnly && fields.name.normalized === query.normalized)
        ? 1000
        : fields.body.normalized.includes(query.normalized) ||
            (!options.bodyOnly && fields.name.normalized.includes(query.normalized))
          ? 40
          : 0;
    ranked.push({ momentIndex, score: score + phraseBonus });
  }
  if (sortResults) {
    ranked.sort((left, right) => compareRankedMoments(left, right, index));
  }
  return ranked;
}

interface RankedPrayerContext {
  prayerIndex: number;
  score: number;
}

interface RememberPrayerRankOptions {
  contentOnly: boolean;
  allowSubstring: boolean;
  exactTokensOnly: boolean;
}

const DIRECT_CONTEXT_RANK_OPTIONS: RememberPrayerRankOptions = {
  contentOnly: false,
  allowSubstring: true,
  exactTokensOnly: false,
};

const RELATED_CONTEXT_RANK_OPTIONS: RememberPrayerRankOptions = {
  // A synonym is a topical retrieval hint, never a reason to reinterpret a
  // person's name (for example career -> a person named Job).
  contentOnly: true,
  allowSubstring: false,
  exactTokensOnly: true,
};

function matchingPrayerTokenIds(
  word: string,
  index: RememberSearchIndex,
  allowSubstring: boolean,
  exactTokensOnly: boolean,
  metrics?: RememberSearchMetrics,
): number[] {
  if (exactTokensOnly) {
    const tokenId = exactSortedTokenId(index.prayerTokenList, word, metrics);
    return tokenId === undefined ? [] : [tokenId];
  }
  const matched: number[] = [];
  for (let tokenId = 0; tokenId < index.prayerTokenList.length; tokenId += 1) {
    if (metrics) metrics.tokenComparisons += 1;
    const token = index.prayerTokenList[tokenId];
    if (
      token === word ||
      (!exactTokensOnly && token.startsWith(word)) ||
      (!exactTokensOnly && allowSubstring && word.length >= 4 && token.includes(word))
    ) matched.push(tokenId);
  }
  return matched;
}

function prayerPostingEstimate(
  tokenIds: readonly number[],
  index: RememberSearchIndex,
): number {
  let estimate = 0;
  for (const tokenId of tokenIds) estimate += index.prayerPostings[tokenId].length;
  return estimate;
}

function prayerWordMatches(
  word: string,
  fields: RememberPrayerFields,
  options: RememberPrayerRankOptions,
): boolean {
  const tiers = options.contentOnly
    ? prayerSemanticTiers(fields)
    : prayerContextTiers(fields);
  return tiers.some((field) => {
    const quality = discoveryWordQuality(word, field);
    return quality > 0 &&
      (options.exactTokensOnly
        ? quality === 3
        : options.allowSubstring || quality > 1);
  });
}

function candidatesForPrayerWord(
  word: string,
  tokenIds: readonly number[],
  index: RememberSearchIndex,
  current: Set<number> | null,
  options: RememberPrayerRankOptions,
  metrics?: RememberSearchMetrics,
): Set<number> {
  const next = new Set<number>();
  if (current) {
    for (const prayerIndex of current) {
      if (metrics) metrics.tokenComparisons += 1;
      if (prayerWordMatches(word, index.prayerFields[prayerIndex], options)) {
        next.add(prayerIndex);
      }
    }
    return next;
  }
  for (const tokenId of tokenIds) {
    for (const posting of index.prayerPostings[tokenId]) {
      if (metrics) metrics.postingVisits += 1;
      const tierMask = posting & 31;
      if (options.contentOnly && (tierMask & 30) === 0) continue;
      next.add(Math.floor(posting / 32));
    }
  }
  return next;
}

function compareRankedPrayerContexts(
  left: RankedPrayerContext,
  right: RankedPrayerContext,
  index: RememberSearchIndex,
): number {
  if (right.score !== left.score) return right.score - left.score;
  const leftMoment = index.moments[index.representativeMomentIndexes[left.prayerIndex]];
  const rightMoment = index.moments[index.representativeMomentIndexes[right.prayerIndex]];
  const date = finiteMomentTime(rightMoment) - finiteMomentTime(leftMoment);
  if (date !== 0) return date;
  return compareDiscoveryStrings(
    index.prayerIds[left.prayerIndex],
    index.prayerIds[right.prayerIndex],
  );
}

function rankPrayerContextQuery(
  query: DiscoveryQuery,
  index: RememberSearchIndex,
  metrics?: RememberSearchMetrics,
  options: RememberPrayerRankOptions = DIRECT_CONTEXT_RANK_OPTIONS,
  sortResults = true,
): RankedPrayerContext[] {
  if (!query.valid || query.tokens.length === 0) return [];
  const groups = query.tokens.map((word) => ({
    word,
    tokenIds: matchingPrayerTokenIds(
      word,
      index,
      options.allowSubstring,
      options.exactTokensOnly,
      metrics,
    ),
  }));
  if (groups.some((group) => group.tokenIds.length === 0)) return [];
  groups.sort(
    (left, right) =>
      prayerPostingEstimate(left.tokenIds, index) -
      prayerPostingEstimate(right.tokenIds, index),
  );

  let candidates: Set<number> | null = null;
  for (const group of groups) {
    candidates = candidatesForPrayerWord(
      group.word,
      group.tokenIds,
      index,
      candidates,
      options,
      metrics,
    );
    if (candidates.size === 0) return [];
  }
  if (!candidates) return [];

  const ranked: RankedPrayerContext[] = [];
  for (const prayerIndex of candidates) {
    if (metrics) metrics.prayerContextEvaluations += 1;
    const fields = options.contentOnly
      ? prayerSemanticTiers(index.prayerFields[prayerIndex])
      : prayerContextTiers(index.prayerFields[prayerIndex]);
    const score = scoreRememberFields(
      query,
      fields,
      options.exactTokensOnly,
    );
    if (score === null) continue;
    const exactPhrase = fields.some((field) => field.normalized === query.normalized);
    const containsPhrase = fields.some((field) =>
      field.normalized.includes(query.normalized),
    );
    ranked.push({
      prayerIndex,
      score: score + (exactPhrase ? 1000 : containsPhrase ? 40 : 0),
    });
  }
  if (sortResults) {
    ranked.sort((left, right) => compareRankedPrayerContexts(left, right, index));
  }
  return ranked;
}

interface StrengthenedMoment extends RankedMoment {
  hits: number;
}

interface StrengthenedPrayerContext extends RankedPrayerContext {
  hits: number;
}

function relatedQueries(
  prepared: DiscoveryQuery,
  expansionTerms: readonly string[],
  metrics?: RememberSearchMetrics,
): DiscoveryQuery[] {
  const seen = new Set<string>([prepared.normalized]);
  const out: DiscoveryQuery[] = [];
  for (const rawTerm of expansionTerms.slice(0, MAX_REMEMBER_SYNONYM_TERMS)) {
    const term = prepareDiscoveryQuery(rawTerm);
    if (!term.valid || term.tokens.length === 0 || seen.has(term.normalized)) continue;
    seen.add(term.normalized);
    out.push(term);
    if (metrics) metrics.relatedTermCount += 1;
  }
  return out;
}

function rankRelatedVisibleMoments(
  terms: readonly DiscoveryQuery[],
  index: RememberSearchIndex,
  metrics?: RememberSearchMetrics,
): StrengthenedMoment[] {
  const bestByPrayer = new Map<number, StrengthenedMoment>();
  for (const term of terms) {
    const bestForTerm = new Map<number, RankedMoment>();
    for (const candidate of rankRememberQuery(
      term,
      index,
      metrics,
      RELATED_RANK_OPTIONS,
      false,
    )) {
      const prayerIndex = index.momentPrayerIndexes[candidate.momentIndex];
      const current = bestForTerm.get(prayerIndex);
      if (!current || compareRankedMoments(candidate, current, index) < 0) {
        bestForTerm.set(prayerIndex, candidate);
      }
    }
    // One synonym term strengthens a prayer once, no matter how many of its
    // moments contain that wording. Pick the term's best visible carrier
    // without sorting the entire result band for every expansion term.
    for (const [prayerIndex, candidate] of bestForTerm) {
      const current = bestByPrayer.get(prayerIndex);
      if (current) {
        current.hits += 1;
        if (compareRankedMoments(candidate, current, index) < 0) {
          current.momentIndex = candidate.momentIndex;
          current.score = candidate.score;
        }
      } else {
        bestByPrayer.set(prayerIndex, { ...candidate, hits: 1 });
      }
    }
  }
  const ranked = [...bestByPrayer.values()];
  ranked.sort((left, right) =>
    right.hits !== left.hits
      ? right.hits - left.hits
      : compareRankedMoments(left, right, index),
  );
  return ranked;
}

function rankRelatedPrayerContexts(
  terms: readonly DiscoveryQuery[],
  index: RememberSearchIndex,
  metrics?: RememberSearchMetrics,
): StrengthenedPrayerContext[] {
  const best = new Map<number, StrengthenedPrayerContext>();
  for (const term of terms) {
    for (const candidate of rankPrayerContextQuery(
      term,
      index,
      metrics,
      RELATED_CONTEXT_RANK_OPTIONS,
      false,
    )) {
      const current = best.get(candidate.prayerIndex);
      if (current) {
        current.hits += 1;
        current.score = Math.max(current.score, candidate.score);
      } else {
        best.set(candidate.prayerIndex, { ...candidate, hits: 1 });
      }
    }
  }
  const ranked = [...best.values()];
  ranked.sort((left, right) =>
    right.hits !== left.hits
      ? right.hits - left.hits
      : compareRankedPrayerContexts(left, right, index),
  );
  return ranked;
}

export function searchRememberIndex(
  index: RememberSearchIndex,
  query: string,
  expansionTerms: string[],
  limit = Number.POSITIVE_INFINITY,
  metrics?: RememberSearchMetrics,
  offset = 0,
): RememberSearchResult {
  const prepared = prepareDiscoveryQuery(query);
  const empty: RememberSearchResult = {
    moments: [],
    total: 0,
    directTotal: 0,
    contextTotal: 0,
    relatedTotal: 0,
    context: false,
    related: false,
    matchKindByMomentId: new Map(),
    scopeKey: index.scopeKey,
    normalizedQuery: prepared.normalized,
    queryTokens: prepared.tokens,
  };
  if (!prepared.valid || prepared.tokens.length === 0) return empty;

  const direct = rankRememberQuery(prepared, index, metrics);
  const literalContext = rankPrayerContextQuery(prepared, index, metrics);
  const terms = relatedQueries(prepared, expansionTerms, metrics);
  const relatedVisible = rankRelatedVisibleMoments(terms, index, metrics);
  const relatedContext = rankRelatedPrayerContexts(terms, index, metrics);
  const relatedVisiblePrayerIndexes = new Set(
    relatedVisible.map((candidate) =>
      index.momentPrayerIndexes[candidate.momentIndex]
    ),
  );

  const ranked: Array<{
    momentIndex: number;
    kind: RememberSearchMatchKind;
  }> = [];
  const includedMomentIndexes = new Set<number>();
  const representedPrayerIndexes = new Set<number>();
  const directPrayerIndexes = new Set<number>();

  const push = (momentIndex: number, kind: RememberSearchMatchKind) => {
    if (includedMomentIndexes.has(momentIndex)) return;
    includedMomentIndexes.add(momentIndex);
    representedPrayerIndexes.add(index.momentPrayerIndexes[momentIndex]);
    ranked.push({ momentIndex, kind });
  };

  // Every card whose own displayed attribution/body matches remains visible.
  for (const candidate of direct) {
    directPrayerIndexes.add(index.momentPrayerIndexes[candidate.momentIndex]);
    push(candidate.momentIndex, 'direct');
  }

  // Wider literal context (raw prayer, AI core/summary, mentioned people, any
  // update, testimony) retrieves one latest positive moment only when no card
  // from that prayer already explains the literal match on its face. When an
  // authored moment visibly carries related wording, prefer that explainable
  // carrier over a newer generic card reached only through hidden context.
  for (const candidate of literalContext) {
    if (
      directPrayerIndexes.has(candidate.prayerIndex) ||
      relatedVisiblePrayerIndexes.has(candidate.prayerIndex)
    ) continue;
    push(index.representativeMomentIndexes[candidate.prayerIndex], 'context');
  }

  // Related wording is always additive, never an emergency-only fallback.
  // The best matching authored body is the most explainable carrier for each
  // prayer. Keeping one related carrier per prayer prevents a prayer with a
  // long positive history from crowding every result page.
  for (const candidate of relatedVisible) {
    const prayerIndex = index.momentPrayerIndexes[candidate.momentIndex];
    if (representedPrayerIndexes.has(prayerIndex)) continue;
    push(candidate.momentIndex, 'related');
  }
  for (const candidate of relatedContext) {
    if (representedPrayerIndexes.has(candidate.prayerIndex)) continue;
    push(index.representativeMomentIndexes[candidate.prayerIndex], 'related');
  }

  const boundedOffset = Number.isFinite(offset) ? Math.max(0, Math.floor(offset)) : 0;
  const boundedLimit = Number.isFinite(limit)
    ? Math.max(0, Math.floor(limit))
    : ranked.length;
  const directTotal = ranked.filter((match) => match.kind === 'direct').length;
  const contextTotal = ranked.filter((match) => match.kind === 'context').length;
  const relatedTotal = ranked.filter((match) => match.kind === 'related').length;
  const page = ranked.slice(boundedOffset, boundedOffset + boundedLimit);
  const matchKindByMomentId = new Map<string, RememberSearchMatchKind>();
  for (const match of page) {
    matchKindByMomentId.set(index.moments[match.momentIndex].id, match.kind);
  }
  return {
    moments: page.map(({ momentIndex }) => index.moments[momentIndex]),
    total: ranked.length,
    directTotal,
    contextTotal,
    relatedTotal,
    context: contextTotal > 0,
    related: relatedTotal > 0,
    matchKindByMomentId,
    scopeKey: index.scopeKey,
    normalizedQuery: prepared.normalized,
    queryTokens: prepared.tokens,
  };
}

export function searchMoments(
  moments: Moment[],
  query: string,
  expansionTerms: string[],
): Moment[] {
  return searchRememberIndex(
    buildRememberSearchIndex(moments),
    query,
    expansionTerms,
  ).moments;
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function isAnniversaryDate(candidate: Date, today: Date): boolean {
  if (
    candidate.getMonth() === today.getMonth() &&
    candidate.getDate() === today.getDate()
  ) {
    return true;
  }
  return (
    !isLeapYear(today.getFullYear()) &&
    today.getMonth() === 1 &&
    today.getDate() === 28 &&
    candidate.getMonth() === 1 &&
    candidate.getDate() === 29
  );
}

export function findAnniversaryMoment(
  moments: Moment[],
  today: Date,
): Moment | null {
  const year = today.getFullYear();
  let testimony: Moment | null = null;
  let testimonyYear = -Infinity;
  let answered: Moment | null = null;
  let answeredYear = -Infinity;
  for (const moment of moments) {
    if (moment.kind === 'progress' || !moment.body) continue;
    const date = new Date(moment.date);
    if (!Number.isFinite(date.getTime()) || !isAnniversaryDate(date, today)) continue;
    const candidateYear = date.getFullYear();
    if (candidateYear >= year) continue;
    if (moment.kind === 'testimony') {
      if (candidateYear > testimonyYear) {
        testimony = moment;
        testimonyYear = candidateYear;
      }
    } else if (candidateYear > answeredYear) {
      answered = moment;
      answeredYear = candidateYear;
    }
  }
  return testimony ?? answered;
}

export { GENERAL_KEY };
